/* Contact page — writes enquiries to Firestore. */
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "../../lib/router-compat.jsx";
import PublicShell from "../../components/shell/PublicShell.jsx";
import { HeroBlock } from "../../components/SectionRenderer.jsx";
import { Button, Field, Input, Textarea, Card, Icon, showToast } from "../../ui/index.js";
import { usePage, useSiteSettings } from "../../data/hooks.js";
import { addEnquiry } from "../../data/mutations.js";

export default function ContactPage() {
  const { settings } = useSiteSettings();
  const contact = settings?.contact || {};
  const { sections } = usePage("contact");
  const hero = sections.find((s) => s.type === "hero");

  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please add your name.";
    if (!form.email.trim()) e.email = "Please add your email so we can reply.";
    else if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "That doesn't look like a valid email.";
    if (!form.message.trim()) e.message = "What would you like to ask?";
    else if (form.message.length < 10) e.message = "Tell us a bit more — at least 10 characters.";
    return e;
  };

  const submit = async (e) => {
    e.preventDefault();
    const v = validate();
    setErrors(v);
    if (Object.keys(v).length) return;
    setSubmitting(true);
    try {
      await addEnquiry({ name: form.name.trim(), email: form.email.trim(), subject: form.subject.trim(), message: form.message.trim() });
      setSuccess({ name: form.name, email: form.email });
    } catch (err) {
      showToast({ intent: "danger", title: "Couldn't send your message", body: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const wa = (contact.whatsappNumber || "").replace(/\D/g, "");

  return (
    <PublicShell>
      <Helmet><title>Contact — Purple Angels Kids Foundation</title></Helmet>
      {hero && <HeroBlock section={hero} narrow />}
      <section className="section">
        <div className="container">
          <div className="split-2-3">
            <div className="stack-lg">
              <div>
                <h2 className="t-2xl" style={{ margin: 0 }}>Reach us directly</h2>
                <p className="t-sm muted" style={{ marginTop: 8 }}>However works for you — we read all of them.</p>
              </div>
              {contact.publicEmail && <ContactCard icon={Icon.Mail} label="Email" value={contact.publicEmail} href={`mailto:${contact.publicEmail}`} />}
              {contact.publicPhone && <ContactCard icon={Icon.Phone} label="Phone" value={contact.publicPhone} href={`tel:${contact.publicPhone}`} />}
              {contact.whatsappNumber && <ContactCard icon={Icon.MessageCircle} label="WhatsApp" value={contact.whatsappNumber} href={`https://wa.me/${wa}`} />}
              <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                {contact.instagram && <a href={contact.instagram} target="_blank" rel="noreferrer" className="btn btn--secondary btn--sm" aria-label="Instagram"><Icon.Instagram size={16} /></a>}
                {contact.facebook && <a href={contact.facebook} target="_blank" rel="noreferrer" className="btn btn--secondary btn--sm" aria-label="Facebook"><Icon.Facebook size={16} /></a>}
              </div>
            </div>

            <Card variant="elevated" style={{ padding: 32 }}>
              {success ? (
                <div style={{ textAlign: "center", padding: "16px 0" }}>
                  <div style={{ width: 64, height: 64, borderRadius: "50%", background: "var(--success-bg)", color: "var(--success)", display: "grid", placeItems: "center", margin: "0 auto 16px" }}>
                    <Icon.Check size={32} />
                  </div>
                  <h2 className="t-2xl" style={{ marginTop: 0 }}>Thanks, {success.name}.</h2>
                  <p className="t-base muted">We got your message and will reply to <strong>{success.email}</strong> within 2 business days.</p>
                  <Button variant="ghost" style={{ marginTop: 16 }} onClick={() => { setSuccess(null); setForm({ name: "", email: "", subject: "", message: "" }); }}>
                    Send another message
                  </Button>
                </div>
              ) : (
                <form onSubmit={submit} noValidate>
                  <h2 className="t-2xl" style={{ marginTop: 0 }}>Send us a message</h2>
                  <p className="t-sm muted" style={{ marginTop: 4, marginBottom: 24 }}>We aim to reply within 2 business days.</p>
                  <div className="stack-lg">
                    <Field label="Your name" required error={errors.name} htmlFor="cf-name">
                      <Input id="cf-name" value={form.name} onChange={update("name")} error={!!errors.name} placeholder="Akua Mensah" />
                    </Field>
                    <Field label="Email address" required error={errors.email} htmlFor="cf-email">
                      <Input id="cf-email" type="email" value={form.email} onChange={update("email")} error={!!errors.email} placeholder="you@example.com" />
                    </Field>
                    <Field label="Subject (optional)" htmlFor="cf-subject">
                      <Input id="cf-subject" value={form.subject} onChange={update("subject")} placeholder="What's this about?" />
                    </Field>
                    <Field label="Message" required error={errors.message} helper="Tell us how we can help." htmlFor="cf-message">
                      <Textarea id="cf-message" value={form.message} onChange={update("message")} error={!!errors.message} rows={6} placeholder="Hello! I'd like to ask about…" />
                    </Field>
                    <Button type="submit" variant="primary" size="lg" loading={submitting} className="btn--full">
                      {submitting ? "Sending..." : "Send message"}
                    </Button>
                    <p className="t-xs muted center" style={{ margin: 0 }}>
                      By submitting, you agree to our <Link to="/privacy">Privacy Notice</Link>.
                    </p>
                  </div>
                </form>
              )}
            </Card>
          </div>
        </div>
      </section>
    </PublicShell>
  );
}

function ContactCard({ icon: Ico, label, value, href }) {
  return (
    <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" style={{ display: "flex", alignItems: "center", gap: 16, padding: 16, border: "1px solid var(--border)", borderRadius: "var(--radius-md)", background: "var(--surface)", color: "var(--text)", textDecoration: "none" }}>
      <div className="icon-circle"><Ico size={20} /></div>
      <div>
        <div className="t-xs muted" style={{ textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
        <div className="t-base" style={{ fontWeight: 500, color: "var(--text)" }}>{value}</div>
      </div>
    </a>
  );
}
